import type { PersonInvolvementKind } from "../types/involvement.js";
import type { OrganisationType } from "../types/organisation.js";

/**
 * The involvement kinds each kind of organisation may hold.
 *
 * A provider is where a person is placed: it holds the placement and not the
 * duty of care, which sits with the local authority. That is a rule rather than
 * a convention, because a duty of care recorded against the wrong kind of
 * organisation puts a person's accountability somewhere it does not sit, and
 * nothing downstream would notice.
 *
 * This is also the whole answer to whether an organisation may hold a kind of
 * involvement at all: `isPartyKindEligibleFor` asks it of this table rather
 * than keeping a second list that could disagree.
 *
 * `created_by` is in every row, `unknown` included, because it is not a part
 * in a person's care that anybody holds: it records which organisation created
 * the person's record, which is a fact about what happened. An organisation of
 * a kind nobody has stated can therefore still be shown as the creator of its
 * own person records, while eligible for nothing else.
 */
const eligibleInvolvementKindsByOrganisationType: Record<
  OrganisationType,
  readonly PersonInvolvementKind[]
> = {
  // Accountable for the person. Whether another kind of organisation ever
  // should be is a product decision, not an omission.
  local_authority: ["duty_of_care", "created_by", "viewer"],
  provider: ["placement", "created_by", "viewer"],
  // A residential unit is one kind of provider, told apart from the rest so a
  // picker can offer them separately.
  residential_unit: ["placement", "created_by", "viewer"],
  education: ["created_by", "viewer"],
  health: ["created_by", "viewer"],
  oversight: ["created_by", "viewer"],
  unknown: ["created_by"],
};

export const isOrganisationTypeEligibleFor = (
  organisationType: OrganisationType,
  kind: PersonInvolvementKind,
): boolean =>
  eligibleInvolvementKindsByOrganisationType[organisationType].includes(kind);
